'use client'

/**
 * Internal staging tool: preview the SOCIAL FEED in another currency without
 * relaunching as a different player. Sets the useSocialCurrencyOverride store
 * that the three social read sites (SocialBetTable / MarbleBetTable /
 * SocialBetItem) consume — it previews the feed ONLY; chips/balance/limits
 * keep the launch currency by design.
 *
 * Presets mirror the Task 10 verification table, including BTC with NO rate:
 * an active override REPLACES the server rate even when its usdRate is
 * undefined, so BTC demonstrates the excluded behaviour ($ + USD values).
 *
 * Visibility (never renders in production — both belts, see
 * useSocialCurrencyOverride): mounted in layout.tsx behind
 * `NEXT_PUBLIC_ENV !== 'production'`, and the read-site hook hard-returns
 * null in production regardless.
 */

import { useEffect, useState } from 'react'
import { isSocialFxDebugEnabled } from './isSocialFxDebugEnabled'
import {
  useSocialCurrencyOverrideStore,
  type SocialCurrencyOverride
} from './useSocialCurrencyOverride'

interface Preset {
  id: string
  label: string
  /** Absent for 'off' (clears the override). */
  currency?: string
  /** Fixed test rate, units per USD. Absent = exercise the no-rate path. */
  usdRate?: number
}

// Mirrors the Task 10 six-currency table + the sweeps/excluded classes.
const PRESETS: Preset[] = [
  { id: 'off', label: 'off (launch currency)' },
  { id: 'USD', label: 'USD', currency: 'USD' },
  { id: 'ZAR', label: 'ZAR', currency: 'ZAR', usdRate: 18.42 },
  { id: 'TRY', label: 'TRY', currency: 'TRY', usdRate: 34.0 },
  { id: 'EUR', label: 'EUR', currency: 'EUR', usdRate: 0.92 },
  { id: 'kIDR', label: 'kIDR', currency: 'kIDR', usdRate: 17.94 },
  { id: 'GLD', label: 'GLD (sweeps)', currency: 'GLD' }, // sweeps table path
  { id: 'GC', label: 'GC (sweeps)', currency: 'GC' },
  { id: 'BTC', label: 'BTC (excluded)', currency: 'BTC' }
]

// Same shell/styling as InventoryDebugPanel, anchored bottom-RIGHT so the two
// staging pills never overlap.
const s = {
  wrap: {
    position: 'fixed',
    right: 12,
    bottom: 12,
    zIndex: 2147483646,
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#e6e6e6'
  } as const,
  pill: {
    background: '#10141c',
    border: '1px solid #2bd17e',
    color: '#2bd17e',
    borderRadius: 999,
    padding: '6px 12px',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
  } as const,
  panel: {
    width: 260,
    background: '#10141c',
    border: '1px solid #2a3344',
    borderRadius: 10,
    padding: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  } as const,
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 } as const,
  input: {
    width: 110,
    background: '#0a0d13',
    border: '1px solid #2a3344',
    color: '#e6e6e6',
    borderRadius: 6,
    padding: '4px 6px'
  } as const,
  btnGhost: {
    background: 'transparent',
    border: '1px solid #46506a',
    color: '#cdd5e4',
    borderRadius: 6,
    padding: '2px 8px',
    cursor: 'pointer'
  } as const,
  label: { color: '#8b97ad' } as const,
  title: { color: '#2bd17e', fontWeight: 700 } as const,
  note: { color: '#8b97ad', lineHeight: 1.35 } as const
}

const buildOverride = (preset: Preset, customRate: string): SocialCurrencyOverride | null => {
  if (preset.currency === undefined) return null
  const parsed = Number(customRate)
  const usdRate =
    customRate.trim() !== '' && Number.isFinite(parsed) && parsed > 0 ? parsed : preset.usdRate
  // usdRate stays ABSENT (not defaulted) when neither the preset nor the
  // custom field provides one — that absence is the point of the BTC/sweeps
  // presets (see useSocialCurrencyOverride).
  return usdRate === undefined
    ? { currency: preset.currency }
    : { currency: preset.currency, usdRate }
}

function useSocialFxDebugEnabled(): boolean {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    // Runs client-side only, so window.location is available. Local dev is
    // detected via NODE_ENV (development) rather than NEXT_PUBLIC_ENV, which
    // is set to `staging` in .env.local — see isSocialFxDebugEnabled (and the
    // isInventoryDebugEnabled precedent it mirrors).
    setEnabled(
      isSocialFxDebugEnabled({
        env: process.env.NEXT_PUBLIC_ENV,
        nodeEnv: process.env.NODE_ENV,
        fxdebugParam: new URLSearchParams(window.location.search).get('fxdebug')
      })
    )
  }, [])
  return enabled
}

export function SocialCurrencyDebugPanel() {
  const enabled = useSocialFxDebugEnabled()
  const [open, setOpen] = useState(false)
  const [presetId, setPresetId] = useState('off')
  const [customRate, setCustomRate] = useState('')
  const override = useSocialCurrencyOverrideStore((state) => state.override)
  const setOverride = useSocialCurrencyOverrideStore((state) => state.setOverride)

  // Panel-visibility belt: never in production, dev auto-on, deployed
  // staging needs ?fxdebug=1 (per-session opt-in — clients testing on shared
  // staging must not see the pill). The read-site belt is
  // useSocialCurrencyOverride's hard null in production.
  if (!enabled) return null

  const apply = (nextPresetId: string, nextCustomRate: string) => {
    setPresetId(nextPresetId)
    setCustomRate(nextCustomRate)
    const preset = PRESETS.find((p) => p.id === nextPresetId)
    setOverride(preset ? buildOverride(preset, nextCustomRate) : null)
  }

  if (!open) {
    return (
      <div style={s.wrap}>
        <button
          onClick={() => {
            setOpen(true)
          }}
          style={s.pill}
          type="button"
        >
          💱 FX debug{override ? ` · ${override.currency}` : ''}
        </button>
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <div style={s.panel}>
        <div style={s.row}>
          <span style={s.title}>Social FX debug</span>
          <button
            onClick={() => {
              setOpen(false)
            }}
            style={s.btnGhost}
            type="button"
          >
            ✕
          </button>
        </div>

        <div style={s.note}>
          <b>FIXED TEST RATES</b> — staging preview of the social feed only; balance/chips keep the
          launch currency.
        </div>

        <div style={s.row}>
          <span style={s.label}>Preset</span>
          <select
            onChange={(e) => {
              // Clear any typed rate on a preset switch: a leftover custom
              // rate would silently give e.g. the BTC preset a rate and
              // defeat its excluded-behaviour demo.
              apply(e.target.value, '')
            }}
            style={s.input}
            value={presetId}
          >
            {PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>

        <div style={s.row}>
          <span style={s.label}>Rate/USD</span>
          <input
            onChange={(e) => {
              apply(presetId, e.target.value)
            }}
            placeholder="preset default"
            style={s.input}
            value={customRate}
          />
        </div>

        <div style={s.note}>
          Active:{' '}
          <b>
            {override
              ? `${override.currency} @ ${override.usdRate ?? 'no rate (USD fallback)'}`
              : 'off'}
          </b>
        </div>
      </div>
    </div>
  )
}
